import { Button, StickyBarContent, Title3 } from '@common/components';
import Portal from '@common/components/Portal';
import { ButtonWrapper } from '@features/DynamicPackagesConfigurationWizard/components/ExperienceDetails/ExperienceDetails.styles';
import { VisitDateForm } from '@features/DynamicPackagesConfigurationWizard/components/VisitDateForm/VisitDateForm';
import {
  ExperienceStepSchema,
  PackagesFormValues,
} from '@features/DynamicPackagesConfigurationWizard/types';
import { useGetSpaPackage } from '@features/spa';
import { useTranslations } from '@lang';
import { some } from '@utils/some';
import moment from 'moment';
import { Fragment, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { SpaTitleWrapper } from '../../components/ExperiencesGroup/ExperiencesGroup.styles';
import { FormStepSectionWrapper, PageTitle, StyledDateRangeIndicator } from '../FormSteps.styles';
import { useGroupOptionsByDateAndOptionGroup } from '../hooks/useGroupOptionsByDate';
import { useHashNavigation } from '../hooks/useHashNavigation';
import { usePackageConfiguration, useWizardNavigation, useCurrentStep } from '../../contexts';
import {
  ExperienceTitleWrapper,
  ExperienceWrapper,
  GuestButtonWrapper,
  GuestName,
  SpaEntitySelectionWrapper,
  SpaEntityStepWrapper,
} from './ExperiencesStep.styles';
import { MultipleOptionsExperienceTimeslotSelection } from './MultipleOptionsExperienceTimeslotSelection/MultipleOptionsExperienceTimeslotSelection';
import { SingleOptionExperienceTimeslotSelection } from './SingleOptionExperienceTimeslotSelection/SingleOptionExperienceTimeslotSelection';

export const ExperiencesTimeslotsStep = () => {
  const {
    property,
    journeyParams,
    spaPackageId,
    isPackageRepeating,
    stayDuration,
    totalGuestAmount,
    rate,
  } = usePackageConfiguration();
  const { stickyBarId, handleNextStep, isLastStep } = useWizardNavigation();
  const { packageTotalPrice, hash, isSubmitting } = useCurrentStep();

  const numberOfGuests = totalGuestAmount;

  useHashNavigation({ hash });

  const { t } = useTranslations();
  const { watch, setValue, getValues } = useFormContext<PackagesFormValues>();
  const selectedExperiences = watch('selectedExperiences');
  const experiences = watch('experiences');

  const cleanedExperiences = {
    ...experiences,
    guestOptions: experiences.guestOptions?.map((opt) => ({
      ...opt,
      timeslots: Array.from(opt.timeslots || [], (ts) => ts ?? []),
    })),
  };

  const isValid = ExperienceStepSchema.safeParse(cleanedExperiences).success;

  const guestsHaveDifferentTimes = watch('experiences.guestsHaveDifferentTimes');
  const guestsByRoom = getValues('guests.rooms');
  const guests = guestsByRoom.flat();
  const guestOptions = watch('experiences.guestOptions');
  useEffect(() => {
    setValue('experiences.guestDetails', guests);

    if (guestOptions) {
      const fixed = guestOptions.map((opt) => ({
        ...opt,
        timeslots: Array.from(opt.timeslots || [], (ts) =>
          (ts ?? []).filter((timeslot) => timeslot !== null)
        ),
      }));
      setValue('experiences.guestOptions', fixed);
    }
  }, []);

  const { data: spaDay } = useGetSpaPackage(
    property.id,
    Number(spaPackageId),
    property.defaultLanguage,
    true
  );

  const selectedExperiencesWithoutExtras = (selectedExperiences || []).filter(
    (exp) => exp.type !== 'spa-extras'
  );

  const selectedExperiencesByDay = useGroupOptionsByDateAndOptionGroup(
    moment(journeyParams.fromDate),
    selectedExperiencesWithoutExtras,
    isPackageRepeating,
    stayDuration,
    numberOfGuests
  );
  selectedExperiencesByDay.forEach((date) => {
    date.options.sort((a, b) => {
      return a.isIncluded === b.isIncluded ? 0 : a.isIncluded ? 1 : -1;
    });
  });

  const isMultiRoom = some(journeyParams?.rooms || []) && (journeyParams?.rooms || []).length > 1;

  function toggleGuestsHaveSameOptions() {
    // filling the array with empty entries to ensure that the user can't move on until they've entered all the details
    // reason for this is that the 2nd array being present is optional but the values within are not
    const emptyOptions = Array.from(
      { length: selectedExperiencesByDay.filter((day) => day?.options.length).length },
      () => []
    );
    const defaultGuest = { timeslots: emptyOptions };
    const singleGuestOption = [guestOptions?.[0] ?? defaultGuest];
    const multiGuestOptions = Array.from({ length: numberOfGuests }).map(
      (_, i) => guestOptions?.[i] ?? defaultGuest
    );

    setValue(
      'experiences.guestOptions',
      guestsHaveDifferentTimes ? singleGuestOption : multiGuestOptions
    );
    setValue('experiences.guestsHaveDifferentTimes', !guestsHaveDifferentTimes);
  }

  return (
    <>
      <ExperienceWrapper>
        <ExperienceTitleWrapper>
          <SpaTitleWrapper>
            <PageTitle>Spa</PageTitle>
          </SpaTitleWrapper>
          <h2 data-cy="page-title">{t('spa.package.packageAvailabilityTitle')}</h2>
        </ExperienceTitleWrapper>
        <FormStepSectionWrapper>
          <SpaEntityStepWrapper>
            <VisitDateForm
              changeJourneyParam={() => {}}
              isTelephoneOnly={false}
              isLoading={false}
              timeslots={[]}
              namePrefix={''}
            />
            <>
              {isMultiRoom ? (
                <></>
              ) : (
                <GuestButtonWrapper>
                  {numberOfGuests > 1 && (
                    <Button isSmall onClick={toggleGuestsHaveSameOptions}>
                      {!guestsHaveDifferentTimes
                        ? t('overnightPackage.spa.differentOptionsForGuests')
                        : t('overnightPackage.spa.sameOptionForGuests')}
                    </Button>
                  )}
                </GuestButtonWrapper>
              )}
              {(selectedExperiencesByDay || []).map(({ date, options }) => {
                let localExperienceIndex = 0;
                return (
                  <Fragment key={date}>
                    {some(options) && (
                      <>
                        <h3>{date}</h3>
                        {!guestsHaveDifferentTimes ? (
                          <SpaEntitySelectionWrapper>
                            {(options || []).map((exp) => {
                              if (exp.isIncluded) {
                                const currentIndex = localExperienceIndex++;
                                return exp.options.map((experience) => {
                                  return (
                                    <SingleOptionExperienceTimeslotSelection
                                      experience={experience}
                                      experienceDate={date}
                                      index={currentIndex}
                                      key={`experience-${experience.dayNumber}-${currentIndex}`}
                                      configurationGroups={spaDay?.configurationGroups || []}
                                      guestIndex={0}
                                    />
                                  );
                                });
                              } else if (some(exp.options)) {
                                const currentIndex = localExperienceIndex++;
                                return (
                                  <MultipleOptionsExperienceTimeslotSelection
                                    experiences={exp}
                                    experienceDate={date}
                                    index={currentIndex}
                                    key={`experience-group-${exp.dayNumber}-${currentIndex}`}
                                    configurationGroups={spaDay?.configurationGroups || []}
                                    guestIndex={0}
                                  />
                                );
                              }
                            })}
                          </SpaEntitySelectionWrapper>
                        ) : (
                          <>
                            {new Array(numberOfGuests).fill({}).map((_, guestIndex) => {
                              const guestName = `${guests[guestIndex].title} ${guests[guestIndex].firstName} ${guests[guestIndex].lastName}`;
                              let guestLocalExperienceIndex = 0;

                              return (
                                <SpaEntitySelectionWrapper key={`spa-guest-${guestIndex}`}>
                                  <GuestName>
                                    <Title3>{`Guest ${guestIndex + 1} - ${guestName}`}</Title3>
                                  </GuestName>
                                  {(options || []).map((exp) => {
                                    if (exp.isIncluded) {
                                      return exp.options.map((experience) => {
                                        const currentIndex = guestLocalExperienceIndex++;
                                        return (
                                          <SingleOptionExperienceTimeslotSelection
                                            experience={experience}
                                            experienceDate={date}
                                            index={currentIndex}
                                            key={`experience-${experience.dayNumber}-${currentIndex}`}
                                            configurationGroups={spaDay?.configurationGroups || []}
                                            guestIndex={guestIndex}
                                          />
                                        );
                                      });
                                    } else if (some(exp.options)) {
                                      const currentIndex = guestLocalExperienceIndex++;
                                      return (
                                        <MultipleOptionsExperienceTimeslotSelection
                                          experiences={exp}
                                          experienceDate={date}
                                          index={currentIndex}
                                          key={`experience-group-${exp.dayNumber}-${currentIndex}`}
                                          configurationGroups={spaDay?.configurationGroups || []}
                                          guestIndex={guestIndex}
                                        />
                                      );
                                    }
                                  })}
                                </SpaEntitySelectionWrapper>
                              );
                            })}
                          </>
                        )}
                      </>
                    )}
                  </Fragment>
                );
              })}
            </>
          </SpaEntityStepWrapper>
        </FormStepSectionWrapper>
      </ExperienceWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={rate.title || ''}
          subtitle={
            <StyledDateRangeIndicator
              fromDate={journeyParams.fromDate}
              toDate={journeyParams.toDate}
            />
          }
          displayedPrice={packageTotalPrice}
          button={
            <ButtonWrapper>
              <Button
                isPrimary
                onClick={handleNextStep}
                disabled={!isValid || isSubmitting}
                isLoading={isSubmitting}
              >
                {isLastStep ? t('product.ctaButton') : t('spa.package.nextStep')}
              </Button>
            </ButtonWrapper>
          }
        />
      </Portal>
    </>
  );
};
